import { skipToken, useQuery } from '@tanstack/react-query'

import env from '~/env'

import { useSentryLogging } from '../shared/useSentryLogging'
import { fetchStatusPage } from './fetchStatusPage'
import {
    IncidentStatusReport,
    isIncidentStatusReport,
    isMaintenanceStatusReport,
    isStatusUpdate,
    isUnresolvedStatusReport,
    MaintenanceStatusReport,
    type StatusUpdate,
} from './schema'
import { getReportData, shouldReplaceLatestReport, StatusPageData } from './utils'

const REFETCH_INTERVAL = 60 * 1000 // 1 minute

export const useBetterStackStatusPageQuery = () => {
    const url = env.VITE_BETTERSTACK_STATUS_PAGE_URL

    const query = useQuery({
        queryKey: ['betterstackStatusPage'],
        queryFn: url ? () => fetchStatusPage(url) : skipToken,
        select: (data): StatusPageData => {
            let latestIncidentReport: IncidentStatusReport | null = null
            let latestMaintenanceReport: MaintenanceStatusReport | null = null
            const statusUpdateById = new Map<string, StatusUpdate>()

            for (const item of data.included) {
                if (isUnresolvedStatusReport(item)) {
                    if (isMaintenanceStatusReport(item)) {
                        if (shouldReplaceLatestReport(item, latestMaintenanceReport)) {
                            latestMaintenanceReport = item
                        }
                    } else if (isIncidentStatusReport(item)) {
                        if (shouldReplaceLatestReport(item, latestIncidentReport)) {
                            latestIncidentReport = item
                        }
                    }
                } else if (isStatusUpdate(item)) {
                    statusUpdateById.set(item.id, item)
                }
            }

            return {
                incident: getReportData(latestIncidentReport, statusUpdateById),
                maintenance: getReportData(latestMaintenanceReport, statusUpdateById),
            }
        },
        refetchInterval: REFETCH_INTERVAL,
        staleTime: REFETCH_INTERVAL, // Consider data fresh for same duration as refetch interval to avoid extra refetches
    })

    useSentryLogging({ key: 'useBetterStackStatusPageQuery', queryResult: query })

    return query
}
